import get from 'lodash/get';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Redirect, Route, Switch, withRouter } from 'react-router-dom';
import styled from 'styled-components/macro';

import { Flex } from 'modsy-ui-kit';

import { WAREHOUSE_CLIENT_URL } from 'config/endpoints';
import analytics from 'services/analytics';
import Footer from 'shared/components/PageFooter';

import AdminModal, { AdminModalButton } from './components/AdminModal';
import PrePurchaseMainAppBarB from './components/PrePurchaseMainAppBarB';
import Account from '../Account';
import DirectPackagePurchase from '../DirectPackagePurchase';
import EmailVerification from '../EmailVerification';
import Project from '../Project';
import Studio from 'screens/Studio';
import Login from 'screens/LoginLegacy';
import SignUp from 'screens/SignUp';
import ResetPasswordRequest from 'screens/ResetPasswordRequest';
import ResetPasswordSubmit from 'screens/ResetPasswordSubmit';
import SetPasswordSubmit from 'screens/SetPasswordSubmit';
import ModsyShop from 'screens/Shop';
import GiftCardThankYou from 'screens/GiftCardThankYou';
import StripeProviderHOC from './components/StripeProviderHOC';
import TalkableProviderHOC, { TalkableDiv } from 'services/Talkable';
import TatariProviderHOC from 'services/Tatari';
import ConvertProviderHOC from 'services/Convert';
import { showAdminModal } from './reducer';
import {
  isAdminUser,
  authData,
  requestUserData,
  authIsLoading
} from 'redux/User';
import {
  getActiveSpaceId
} from 'redux/Studio';
import PrivateRoute from '../../PrivateRoute';
import routes from '../../ts/studio/routes';
import ErrorPage from '../../ts/studio/components/error-page';

import { FEATURE_FLAGS, getFeatureFlagEnabledBool } from 'shared/reducers/featureFlag';

import { getDebugString } from 'redux/Debug';

import { QUERY_FOR_PHONE_ONLY, QUERY_FOR_SMALL_TABLET_ONLY } from 'web-ui-kit/media';

import {
  setIsIPad,
  setIsMobile,
  setIsScreenWidthSmall,
  setScreenDimensions,
  setHasWebGL,
  setOrientation
} from 'shared/actions/device';

import MaintenancePage from 'shared/images/maintenance.png';
import MaintenancePageMobile from 'shared/images/maintenance-mobile.png';

import { hasWebGL } from 'utils/utils';
import { ENVIRONMENTS } from 'utils/constants';
import LoadingOverlay from 'shared/components/LoadingOverlay';

const PostPurchaseMonarch = React.lazy(() => (
  import('../PPOMonarch/PostPurchase' /* webpackChunkName: "PostPurchaseMonarch" */)
));

const LandingPageMonarch = React.lazy(() => (
  import('../PPOMonarch/Landing/LandingPage' /* webpackChunkName: "LandingPageMonarch" */)
));

const RouteWithSubRoutes = route => (
  <Route
    path={route.path}
    render={props => (
      // pass the sub-routes down to keep nesting
      <route.component {...props} routes={route.routes} />
    )}
  />
);

const WP_PROJECT_ROUTES = [
  '/project/room',
  '/project/goal',
  '/project/furniture',
  '/project/budget',
  '/project/style',
  '/project/account',
  '/modsy-gift-cards/packages',
  '/modsy-gift-cards/information'
];

const ROUTES_WITH_PREPURCHASE_APP_BAR = [
  '/terms-of-service'
];

const ROUTES_WITH_FOOTER = [
  '/terms-of-service'
];

const ROUTES = {
  homePage: '/',
  localhost: 'localhost',
  direct: '/direct',
  project: '/project',
  termsOfServiceUrl: '/terms-of-service',
  purchase: '/purchase',
  emailVerification: '/email-verification/:token',
  share: '/share',
  friend: '/friend',
  referrals: '/referrals',
  faq: '/faq',
  testQuiz: '/testQuiz',
  styleQuiz: '/stylequiz',
  yourRecommendation: '/project/your-recommendation',
  spacesID: '/spaces/:id',
  spaces: '/spaces',
  spaceViewToken: '/space-view/:user_id/:space_id/:token',
  studioShared: '/studio/shared',
  studio: '/studio',
  projectLuxeThankYou: '/project/luxe/thankyou/:spaceId',
  projectThankYou: '/project/thankyou/:spaceId',
  account: '/account',
  login: '/login',
  signUp: '/sign-up',
  resetPassword: '/reset-password',
  resetToken: '/reset/:token',
  setPassword: '/set-password/:token',
  shop: '/shop*',
  giftcardThankYou: '/gift-card/thankyou'
};

const HELP_URL = 'https://help.modsy.com';

const DEV_ENVIRONMENTS = ENVIRONMENTS.slice(0, 2);

export class AppContainer extends Component {
  constructor (props) {
    super(props);

    this.state = { windowLoaded: false, stripeInstance: null };

    this.isiOS = /(iPad|iPhone|iPod)/g.test(navigator.userAgent);
    this.isChromeiOS = /CriOS/i.test(navigator.userAgent);
    this.props.setIsMobile(/Mobi|Android/i.test(navigator.userAgent));
    this.props.setIsIPad(/iPad/i.test(navigator.userAgent));
    this.updateWindowDimensions();
    this.orientationData = { alpha: 0, beta: 0, gamma: 0 };

    window.addEventListener('resize', this.updateWindowDimensions);
    window.addEventListener('orientationchange', this.handleOrientationChange);
    window.addEventListener('load', () => {
      this.setState({ windowLoaded: true });
    });
    window.addEventListener(
      'deviceorientation',
      (orientationData) => {
        this.orientationData = orientationData;
      },
      false
    );
  }

  handleOrientationChange = (evt) => {
    this.updateWindowDimensions();

    // update orientation
    if (evt.currentTarget.orientation === 0) {
      this.orientation = 'portrait';
    } else {
      this.orientation = 'landscape';
    }

    this.props.setScreenOrientation(this.orientation);

    // Chrome iOS bug reproducing on iOS 10 (have not tested on other iOS versions).
    // Only reproduces on Chrome iOS, does not reproduce on Safari iOS.
    // After rotating from landscape back to portrait, the CSS styled elements
    // are layed out incorrectly. Was very easy to see navbar as it was off the screen
    // to the right. In that state click events did not work and if you managed to open
    // PDP it was also completely messed up.  Its as if certain elements still were layed
    // out as if the screen width was still landscape size.
    //
    // This hack fixes the issue.
    if (this.isChromeiOS) {
      window.scrollTo(0, 1);
    }
  }

  updateWindowDimensions = (initial = true) => {
    // iOS does not always report correct innerWidth and innerHeight values.
    // In particular, when zoomed and in landscape these values may be incorrect.
    // Treat this as an evolving calculation that will likely need to be updated
    // as we find issues or iOS changes.  Definitely open to other solutions here.
    //
    // The ideal goal here is to compute the usable viewport width and height
    // so we can size all our elements correctly. This usable viewport does NOT
    // include the address bar or bottom bar.
    //
    // Note: there is a ScreenSizeDebug feature flag to help debug these screen size issues.
    const width = Math.max(window.innerWidth, get(document, 'documentElement.clientWidth', 0));
    const height = Math.max(window.innerHeight, get(document, 'documentElement.clientHeight', 0));
    const isScreenWidthSmall = width < 768;
    const isPhone = window.matchMedia(QUERY_FOR_PHONE_ONLY).matches;
    const isTablet = window.matchMedia(QUERY_FOR_SMALL_TABLET_ONLY).matches;
    // Performance optimization.
    // If nothing has changed then elide the update.
    if (
      width === this.width &&
      height === this.height &&
      isScreenWidthSmall === this.isScreenWidthSmall &&
      isPhone === this.isPhone &&
      isTablet === this.isTablet
    ) {
      return;
    }

    // Apply changes.
    this.width = width;
    this.height = height;
    this.isPhone = isPhone;
    this.isTablet = isTablet;
    this.isScreenWidthSmall = isScreenWidthSmall;

    // Update the redux device state.
    this.props.setScreenDimensions({
      isScreenWidthSmall: this.isScreenWidthSmall,
      isPhone: this.isPhone,
      isTablet: this.isTablet,
      screenWidth: this.width,
      screenHeight: this.height
    });

    // iOS resize events sometimes fire before layout has been updated.
    // This is a hail mary to simulate follow on resize events
    // one of which will hopefully fire after layout has completed.
    if (initial && this.isiOS) {
      setTimeout(() => this.updateWindowDimensions(!initial), 0);
      setTimeout(() => this.updateWindowDimensions(!initial), 100);
      setTimeout(() => this.updateWindowDimensions(!initial), 500);
      setTimeout(() => this.updateWindowDimensions(!initial), 1000);
      setTimeout(() => this.updateWindowDimensions(!initial), 2000);
    }
  };

  componentDidMount () {
    this.props.getAuthData();
    this.props.setHasWebGL(hasWebGL());
  }

  handleSetStripeInstance = (stripeInstance) => {
    this.setState({ stripeInstance });
  }

  render () {
    const state = this.state;
    const {
      authData,
      activeSpaceId,
      debugString,
      downForMaintenance,
      isAdminUser,
      isAuthLoading,
      isDevEnvs,
      isMobile,
      location,
      showAdminModal,
      tatariEnabled
    } = this.props;

    // Intercept homepage routes and send then to WP marketing app.
    const isHomePage = window.location.pathname === ROUTES.homePage;
    const isLocalHost = window.location.hostname === ROUTES.localhost;
    if (isHomePage) {
      const reloadUrl = isLocalHost
        ? WAREHOUSE_CLIENT_URL
        : window.location.href;

      window.location.replace(reloadUrl);
      return null;
    }

    // Intercept TOS route and send to WP TOS page
    const isTOSPage = window.location.pathname.includes(ROUTES.termsOfServiceUrl);
    if (isTOSPage) {
      window.location.replace('https://www.modsy.com/terms-of-service/');
      return null;
    }

    // Intercept project onboarding recommendations routes and send then to WP marketing app.
    const yourRecsPath = ROUTES.yourRecommendation;
    const isYourRecsPath = location.pathname === yourRecsPath;

    if (isYourRecsPath) {
      const {
        hostname,
        href,
        search
      } = window.location;
      const isLocalHost = hostname === ROUTES.localhost;
      const searchPath = `${WAREHOUSE_CLIENT_URL}${yourRecsPath}${search}`;
      const reloadUrl = isLocalHost ? searchPath : href;

      window.location.replace(reloadUrl);
      return null;
    }

    // Intercept project onboarding routes and send to the WP marketing app
    const wpProjectPath = ROUTES.project;
    const isWPProjectPath = WP_PROJECT_ROUTES.indexOf(location.pathname) !== -1;

    if (isWPProjectPath) {
      const {
        hostname,
        href,
        search
      } = window.location;
      const isLocalHost = hostname === ROUTES.localhost;
      const searchPath = `${WAREHOUSE_CLIENT_URL}${wpProjectPath}${search}`;
      const reloadUrl = isLocalHost ? searchPath : href;

      window.location.replace(reloadUrl);
      return null;
    }
    /**
     * This is just a temporary fix, in the future we will route users directly to the nextjs app
     */
    const rerouteToProjectPage = location &&
      location.pathname.includes(ROUTES.studio) &&
      !(location.state && location.state.noRedirect) &&
      !location.pathname.includes('/design') &&
      !location.pathname.includes('/redesign') &&
      !location.pathname.includes('/3d-style-editor') && // avoid rerouting when accessing bessie from studio
      !location.pathname.includes('/cart');

    if (rerouteToProjectPage && activeSpaceId) {
      window.location.replace(`/projects/${activeSpaceId}`);
      return null;
    }

    /**
     * This is just a temporary fix, in the future we will route users directly to the nextjs app
     */
    const rerouteToDesignPage = location &&
      location.pathname.includes(ROUTES.studio) &&
      !(location.state && location.state.noRedirect) &&
      location.pathname.includes('/design') &&
      !location.pathname.includes('/cart');

    if (rerouteToDesignPage) {
      // Update pathname to match new route pattern
      const updatedPath = location.pathname.replace('/studio', '/projects').replace('/design', '/designs');
      window.location.replace(updatedPath);
      return null;
    }

    /**
     * Maintenance page for when we need to take DB down.
     * Default enable the 'DownForMaintenance' feature flag
     * in featureFlag reducer to activate.
     */
    if (downForMaintenance) {
      return (
        <Flex align='center' justify='center' style={{ margin: 'auto auto' }}>
          {isMobile
            ? (
              <img
                src={MaintenancePageMobile}
                alt='maintenance'
                style={{ height: 'calc(100vh - 32px)' }}
              />
            )
            : (
              <img
                src={MaintenancePage}
                alt='maintenance'
                style={{ maxHeight: '100vh', maxWidth: '100vw' }}
              />
            )}
        </Flex>
      );
    }

    if (isAuthLoading) { return <LoadingOverlay transparent={false} />; }

    return (
      <StripeProviderHOC
        stripeInstance={state.stripeInstance}
        onSetStripeInstance={this.handleSetStripeInstance}
      >
        <TalkableProviderHOC
          authData={authData}
          enabled
          windowLoaded={state.windowLoaded}
          location={location}
        >
          <TatariProviderHOC
            authData={authData}
            enabled={tatariEnabled}
            windowLoaded={state.windowLoaded}
          >
            <ConvertProviderHOC
              authData={authData}
              windowLoaded={state.windowLoaded}
              location={location}
            >
              <div id='outer-container'>
                {isDevEnvs && debugString &&
                  <DebugDataContainer>
                    {debugString}
                  </DebugDataContainer>}
                <Switch>
                  {/* render pre-purchase app bar on relevant pages only */}
                  {ROUTES_WITH_PREPURCHASE_APP_BAR.map(route => (
                    <Route
                      path={route}
                      key={route}
                      component={PrePurchaseMainAppBarB}
                    />
                  ))}
                </Switch>

                <React.Suspense fallback={null}>
                  <Switch>
                    <Route exact path={ROUTES.login} component={Login} />
                    <Route exact path={ROUTES.signUp} component={SignUp} />
                    <Route exact path={ROUTES.resetPassword} component={ResetPasswordRequest} />
                    <Route exact path={ROUTES.resetToken} component={ResetPasswordSubmit} />
                    <Route exact path={ROUTES.setPassword} component={SetPasswordSubmit} />

                    <PrivateRoute
                      path={ROUTES.shop}
                      component={props => <ModsyShop {...props} stripeInstance={state.stripeInstance} />}
                    />

                    <Route path={ROUTES.direct} component={DirectPackagePurchase} />

                    {/* Spaces */}
                    <PrivateRoute
                      path={ROUTES.spacesID}
                      component={PostPurchaseMonarch}
                    />
                    <PrivateRoute
                      path={ROUTES.spaces}
                      component={PostPurchaseMonarch}
                    />
                    <Route
                      path={ROUTES.spaceViewToken}
                      component={Studio}
                    />

                    {/* Studio */}
                    <Route
                      path={ROUTES.studioShared}
                      component={Studio}
                    />
                    <PrivateRoute
                      path={ROUTES.studio}
                      component={Studio}
                    />

                    {/* Project */}
                    <PrivateRoute
                      exact
                      path={ROUTES.projectLuxeThankYou}
                      component={LandingPageMonarch}
                    />

                    <PrivateRoute
                      exact
                      path={ROUTES.projectThankYou}
                      component={PostPurchaseMonarch}
                    />

                    <PrivateRoute
                      exact
                      path={ROUTES.giftcardThankYou}
                      component={GiftCardThankYou}
                    />

                    {/* Account */}
                    <PrivateRoute
                      path={ROUTES.account}
                      component={Account}
                    />

                    {routes.map((route, i) => {
                      return <RouteWithSubRoutes key={i} {...route} />;
                    })}

                    <Route
                      path={ROUTES.project}
                      component={Project}
                    />

                    <Redirect exact path={ROUTES.purchase} to='/' />

                    <Route
                      exact
                      path={ROUTES.faq}
                      render={() => {
                        window.location.href = HELP_URL;
                        return null;
                      }}
                    />

                    <Route
                      exact
                      path={ROUTES.emailVerification}
                      component={EmailVerification}
                    />

                    {/* Talkable */}
                    <Route
                      exact
                      path={ROUTES.share}
                      component={() => <TalkableDiv {...this.props} />}
                    />

                    <Route
                      exact
                      path={ROUTES.friend}
                      component={() => <TalkableDiv {...this.props} />}
                    />

                    <PrivateRoute
                      exact
                      path={ROUTES.referrals}
                      component={() => <TalkableDiv {...this.props} />}
                    />

                    <Route component={ErrorPage} />
                  </Switch>
                </React.Suspense>

                {/* Footer */}
                <Switch>
                  {/* footer added this way because these routes still
                  in old client setup */}
                  {ROUTES_WITH_FOOTER.map(route => (
                    <Route path={route} key={route} component={Footer} />
                  ))}
                </Switch>

                {isAdminUser && <AdminModalButton />}
                {showAdminModal && <AdminModal />}
              </div>
            </ConvertProviderHOC>
          </TatariProviderHOC>
        </TalkableProviderHOC>
      </StripeProviderHOC>
    );
  }
}

const App = connect(
  state => {
    const FF = FEATURE_FLAGS;
    const userAuthData = authData(state);
    const location = state.router.location;
    const isMobile = get(state, 'device.isMobile', false);
    const isAdminUserBool = isAdminUser(state);
    const showAdminModalBool = showAdminModal(state);
    const isAuthLoading = authIsLoading(state);

    const environment = process.env.REACT_APP_ENV;
    const isDevEnvs = DEV_ENVIRONMENTS.includes(environment);

    /* Feature Flags */
    const downForMaintenance = getFeatureFlagEnabledBool(FF.DownForMaintenance, state);
    const tatariEnabled = getFeatureFlagEnabledBool(FF.TatariProvider, state);
    const activeSpaceId = getActiveSpaceId(state);

    const debugString = getDebugString(state);

    return {
      authData: userAuthData,
      activeSpaceId,
      debugString,
      downForMaintenance,
      isAdminUser: isAdminUserBool,
      isAuthLoading,
      isDevEnvs,
      isMobile,
      location,
      showAdminModal: showAdminModalBool,
      tatariEnabled
    };
  },
  dispatch => {
    return {
      getAuthData: () => dispatch(requestUserData()),
      trackPageView: location => dispatch(analytics.trackPageView(location)),
      setIsIPad: isIPad => dispatch(setIsIPad(isIPad)),
      setIsMobile: isMobile => dispatch(setIsMobile(isMobile)),
      setIsScreenWidthSmall: isSmall => dispatch(setIsScreenWidthSmall(isSmall)),
      setScreenDimensions: screenDimensions => dispatch(setScreenDimensions(screenDimensions)),
      setHasWebGL: hasWebGL => dispatch(setHasWebGL(hasWebGL)),
      setScreenOrientation: orientation => dispatch(setOrientation(orientation))
    };
  }
)(AppContainer);
export default withRouter(App);

const DebugDataContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 9999;
`;
